import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  TextField,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { ConfirmDialog, GGDialog, GGDialogTitle } from './StyledDialogs';
import { scrollbarStyles } from './StyledComponents';
import { FANTASY_COLORS } from '../../constants/gameConstants';
import { useGame } from '../../context/GameContext';

// Clear Grid Modal
export const ClearGridModal = ({ open, onClose, onConfirm }) => (
  <ConfirmDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <GGDialogTitle>Clear Grid</GGDialogTitle>
    <DialogContent sx={{ mt: 2 }}>
      <Typography sx={{ fontFamily: 'MedievalSharp' }}>
        Are you sure you want to clear all characters and terrain? This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ p: 2, gap: 1 }}>
      <Button onClick={onClose} variant="contained" sx={{ fontFamily: 'MedievalSharp' }}>
        Cancel
      </Button>
      <Button onClick={onConfirm} variant="outlined" color="error" sx={{ fontFamily: 'MedievalSharp' }}>
        Clear Grid
      </Button>
    </DialogActions>
  </ConfirmDialog>
);

// User Name Input Modal
export const UserNameInputModal = ({ open, onClose, onSubmit, defaultName = '' }) => {
  const [name, setName] = useState(defaultName);

  useEffect(() => {
    setName(defaultName);
  }, [defaultName]);

  const handleSubmit = () => {
    if (name.trim()) {
      onSubmit(name);
    }
  };

  return (
    <GGDialog open={open} onClose={onClose}>
      <GGDialogTitle>Enter Your Name</GGDialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="outlined" disabled={!name.trim()}>
          Join Game
        </Button>
      </DialogActions>
    </GGDialog>
  );
};

// Confirm Leave Modal
export const ConfirmLeaveModal = ({ open, onClose, onConfirm, isCreator }) => {
  return (
    <ConfirmDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <GGDialogTitle>
        {isCreator ? "Close Room" : "Leave Room"}
      </GGDialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Typography sx={{ fontFamily: 'MedievalSharp' }}>
          Are you sure you want to {isCreator ? "close" : "leave"} the room? 
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{ fontFamily: 'MedievalSharp' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm}
          color="error"
          sx={{ fontFamily: 'MedievalSharp' }}
        >
          {isCreator ? "Close Room" : "Leave Room"}
        </Button>
      </DialogActions>
    </ConfirmDialog>
  );
};

// Room Deleted Dialog
export const RoomDeletedDialog = () => {
  const navigate = useNavigate();
  const {
    showRoomDeletedDialog: open,
    roomDeletedMessage: message,
    setShowRoomDeletedDialog,
    setRoomDeletedMessage,
  } = useGame();

  const onClose = useCallback(() => {
    setShowRoomDeletedDialog(false);
    setRoomDeletedMessage('');
    navigate('/');
  }, [navigate, setShowRoomDeletedDialog, setRoomDeletedMessage]);

  return (
    <ConfirmDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <GGDialogTitle>Room Closed</GGDialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Typography sx={{ fontFamily: 'MedievalSharp', fontSize: '1.1rem', textAlign: 'center' }}>
          {message || 'The room has been closed by the creator.'}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ fontFamily: 'MedievalSharp', minWidth: 120 }}
        >
          OK
        </Button>
      </DialogActions>
    </ConfirmDialog>
  );
};

// Coin Transfer Modal
const CoinAvatar = styled(Avatar)(({ theme, color, isEnemy }) => ({
  backgroundColor: color,
  border: `2px solid ${isEnemy ? theme.palette.error.main : theme.palette.background.main}`,
  boxShadow: `0 2px 4px rgba(0,0,0,0.2)`,
  width: 40,
  height: 40
}));

export const CoinTransferModal = ({ 
  open, 
  onClose, 
  availableCoins, 
  onConfirm, 
  sourceMapName,
  targetMapName
}) => {
  const [selectedCoins, setSelectedCoins] = useState([]);
  const [selectAll, setSelectAll] = useState(true);

  useEffect(() => {
    if (open && availableCoins.length > 0) {
      setSelectedCoins(availableCoins.map(coin => coin.id));
      setSelectAll(true);
    }
  }, [open, availableCoins]);

  const handleToggleAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    
    if (checked) {
      setSelectedCoins(availableCoins.map(coin => coin.id));
    } else {
      setSelectedCoins([]);
    }
  };

  const handleToggleCoin = (coinId) => {
    const currentIndex = selectedCoins.indexOf(coinId);
    const newSelectedCoins = [...selectedCoins];

    if (currentIndex === -1) {
      newSelectedCoins.push(coinId);
    } else {
      newSelectedCoins.splice(currentIndex, 1);
    }

    setSelectedCoins(newSelectedCoins);
    setSelectAll(newSelectedCoins.length === availableCoins.length);
  };

  const handleConfirm = () => {
    const coinsToTransfer = availableCoins.filter(coin => 
      selectedCoins.includes(coin.id)
    );
    onConfirm(coinsToTransfer);
  };

  const getColorName = (colorHex) => {
    const colorObj = FANTASY_COLORS.find(c => c.color.toLowerCase() === colorHex.toLowerCase());
    return colorObj ? colorObj.name : 'Unknown';
  };

  return (
    <ConfirmDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <GGDialogTitle>
        Transfer Coins to New Active Map
      </GGDialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Typography sx={{ fontFamily: 'MedievalSharp', mb: 2 }}>
          Select the coins from "<strong>{sourceMapName}</strong>" to copy over to "<strong>{targetMapName}</strong>".
        </Typography>
        
        <FormControlLabel
          control={
            <Checkbox
              checked={selectAll}
              onChange={handleToggleAll}
              color="primary"
            />
          }
          label="Select All Coins"
          sx={{ mb: 2 }}
        />
        
        <List sx={{ 
          maxHeight: '300px', 
          overflow: 'auto',
          ...scrollbarStyles,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
        }}>
          {availableCoins.length > 0 ? (
            availableCoins.map((coin) => (
              <ListItem 
                key={coin.id} 
                onClick={() => handleToggleCoin(coin.id)}
                sx={{ 
                  borderBottom: '1px solid #f0f0f0',
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                <Checkbox
                  edge="start"
                  checked={selectedCoins.includes(coin.id)}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemAvatar>
                  <CoinAvatar color={coin.color} src={coin.image} isEnemy={coin.isEnemy} />
                </ListItemAvatar>
                <ListItemText 
                  primary={coin.name || getColorName(coin.color)}
                  secondary={`${coin.isEnemy ? 'Enemy' : 'Character'}${coin.currentHealth ? ` • HP: ${coin.currentHealth}/${coin.maxHealth}` : ''}`}
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText primary="No coins available to transfer" />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{ fontFamily: 'MedievalSharp' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="outlined"
          color="primary"
          sx={{ fontFamily: 'MedievalSharp' }}
          startIcon={<SwapHorizIcon />}
        >
          Activate Map
        </Button>
      </DialogActions>
    </ConfirmDialog>
  );
};

// Map Dialogs
export const CreateMapDialog = ({ open, onClose, onConfirm, isLoading, mapName, setMapName }) => {
  return (
    <GGDialog open={open} onClose={onClose}>
      <GGDialogTitle>Create New Map</GGDialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Map Name"
          fullWidth
          value={mapName}
          onChange={(e) => setMapName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onConfirm}
          variant="outlined" 
          disabled={!mapName.trim() || isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : "Create"}
        </Button>
      </DialogActions>
    </GGDialog>
  );
};

export const RenameMapDialog = ({ open, onClose, onConfirm, currentName, isLoading }) => {
  const [mapName, setMapName] = useState(currentName || '');

  useEffect(() => {
    setMapName(currentName || '');
  }, [currentName]);

  return (
    <GGDialog open={open} onClose={onClose}>
      <GGDialogTitle>Rename Map</GGDialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="New Map Name"
          fullWidth
          value={mapName}
          onChange={(e) => setMapName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={() => onConfirm(mapName)}
          variant="outlined" 
          disabled={!mapName.trim() || isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : "Rename"}
        </Button>
      </DialogActions>
    </GGDialog>
  );
};

export const DeleteMapDialog = ({ open, onClose, onConfirm, isLoading }) => {
  return (
    <ConfirmDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <GGDialogTitle>Delete Map</GGDialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Typography sx={{ fontFamily: 'MedievalSharp' }}>
          Are you sure you want to delete this map? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{ fontFamily: 'MedievalSharp' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm}
          variant="outlined"
          color="error"
          sx={{ fontFamily: 'MedievalSharp' }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : "Delete Map"}
        </Button>
      </DialogActions>
    </ConfirmDialog>
  );
};

